import { FC, useCallback, useState } from 'react';

import { CheckCircleIcon, RefreshIcon } from '@heroicons/react/outline';

import { ShareChannel } from '../containers/GiftCouponShareModal/GiftCouponShareModal';

const CHANNEL_LABEL: Record<ShareChannel, string> = {
  kakao: '카카오톡',
  sms: '문자',
  other: '다른 앱',
};

interface ShareConfirmModalProps {
  open: boolean;
  isSaving: boolean;
  channel: ShareChannel;
  onConfirm: (memo: string) => void;
  onDeny: () => void;
  onLater: () => void;
  onClose: () => void;
}

export const ShareConfirmModal: FC<ShareConfirmModalProps> = ({
  open,
  isSaving,
  channel,
  onConfirm,
  onDeny,
  onLater,
  onClose,
}) => {
  const [memo, setMemo] = useState('');
  const [step, setStep] = useState<'confirm' | 'retry'>('confirm');

  const handleConfirm = useCallback(() => {
    onConfirm(memo);
    setMemo('');
    setStep('confirm');
  }, [memo, onConfirm]);

  const handleNotYet = useCallback(() => {
    setStep('retry');
  }, []);

  const handleRetryConfirm = useCallback(() => {
    setStep('confirm');
    setMemo('');
    onDeny();
  }, [onDeny]);

  const handleClose = useCallback(() => {
    setStep('confirm');
    setMemo('');
    onClose();
  }, [onClose]);

  const handleBackdropClick = useCallback(() => {
    if (step === 'retry' && !isSaving) {
      handleClose();
    }
  }, [step, isSaving, handleClose]);

  if (!open) return null;

  const channelName = CHANNEL_LABEL[channel];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-6"
      onClick={handleBackdropClick}
    >
      <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
        {step === 'confirm' ? (
          <>
            <div className="mb-4 flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-purple-100">
                <CheckCircleIcon className="h-6 w-6 text-purple-600" />
              </div>
              <h3 className="text-lg font-bold text-gray-800">보내셨나요?</h3>
            </div>

            <p className="mb-3 break-keep text-sm leading-relaxed text-gray-700">
              {channelName}으로 답례품을 잘 보내셨나요?
            </p>

            <div className="mb-4">
              <p className="mb-0.5 break-keep text-xs leading-relaxed text-gray-400">
                누구에게 보냈는지 간단히 메모해 두시면 나중에 확인할 때 편해요.
              </p>
              <p className="mb-1.5 text-xs text-gray-300">
                적지 않으셔도 괜찮아요. 참고용이에요.
              </p>
              <input
                type="text"
                value={memo}
                onChange={(e) => setMemo(e.target.value)}
                placeholder="예: 홍길동"
                maxLength={30}
                className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm placeholder:text-gray-300 focus:border-purple-400 focus:outline-none focus:ring-1 focus:ring-purple-400"
              />
            </div>

            <div className="flex gap-2">
              <button
                onClick={handleNotYet}
                disabled={isSaving}
                className="w-2/5 rounded-xl border border-gray-300 py-3 text-sm font-medium text-gray-600 active:bg-gray-50"
              >
                아직이요
              </button>
              <button
                onClick={handleConfirm}
                disabled={isSaving}
                className={`w-3/5 rounded-xl bg-purple-600 py-3 text-sm font-bold text-white active:bg-purple-700 ${
                  isSaving ? 'opacity-50' : ''
                }`}
              >
                {isSaving ? '확인 중...' : '네, 보냈어요'}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="mb-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-100">
                  <RefreshIcon className="h-6 w-6 text-amber-600" />
                </div>
                <h3 className="text-lg font-bold text-gray-800">다시 보내볼까요?</h3>
              </div>
              <button
                onClick={handleClose}
                disabled={isSaving}
                className="flex h-8 w-8 items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 active:bg-gray-200"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>

            <p className="mb-1.5 break-keep text-sm leading-relaxed text-gray-700">
              {channelName}에서 보내실 분을 못 찾으셨나요?
            </p>
            <p className="mb-5 break-keep text-sm leading-relaxed text-gray-700">
              문자나 다른 방법으로도 전달할 수 있어요.
            </p>

            <div className="flex gap-2">
              <button
                onClick={() => { setStep('confirm'); setMemo(''); onLater(); }}
                disabled={isSaving}
                className="w-2/5 rounded-xl border border-gray-300 py-3 text-sm font-medium text-gray-600 active:bg-gray-50"
              >
                나중에 할게요
              </button>
              <button
                onClick={handleRetryConfirm}
                disabled={isSaving}
                className={`w-3/5 rounded-xl bg-purple-600 py-3 text-sm font-bold text-white active:bg-purple-700 ${
                  isSaving ? 'opacity-50' : ''
                }`}
              >
                {isSaving ? '확인 중...' : '다른 방법으로 보내기'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
